/**
 * Hooks for Course Permissions and Progress.
 *
 * Replaces Wized requests: get_course_permissions, get_user_course_progress,
 *   markVideoComplete
 * Replaces Wized elements: course-item, course-item-name, course-expiry-date,
 *   course_progress, course_progress_bar, expiry_warning, extend_button,
 *   days-remaining, days-remaining-icon, etc.
 */

import { useAsync, useMutation } from "./useAsync";
import * as api from "../lib/api";
import { useAuth } from "../lib/auth";
import type {
  CoursePermissionWithCourse,
  UserCourseProgressRow,
} from "../lib/schema";
import { daysRemaining, isExpired, progressPercent } from "../lib/helpers";

/** Fetch the current user's course permissions. */
export function useCoursePermissions() {
  const { user } = useAuth();
  return useAsync(
    () => api.getCoursePermissions(user!.id),
    user ? [user.id] : null,
  );
}

/** Fetch course-level progress for the current user. */
export function useUserCourseProgress() {
  return useAsync(() => api.getUserCourseProgress(), []);
}

/** Fetch per-video completion status for the current user. */
export function useUserVideoProgress() {
  const { user } = useAuth();
  return useAsync(
    () => api.getUserVideoProgress(user!.id),
    user ? [user.id] : null,
  );
}

/** Check if a specific video has been completed. */
export function isVideoCompleted(
  videoProgress: Array<{ video_id: string; completed: boolean | null }> | null,
  videoId: string,
): boolean {
  if (!videoProgress) return false;
  return videoProgress.some((vp) => vp.video_id === videoId && vp.completed);
}

/** Mutation: mark a video as completed. */
export function useMarkVideoComplete() {
  const { user } = useAuth();
  return useMutation(async (videoId: string) => {
    if (!user) return { data: null, error: { message: "Not authenticated" } };
    return api.markVideoComplete(user.id, videoId);
  });
}

// ---------------------------------------------------------------------------
// Derived data helpers
// ---------------------------------------------------------------------------

/** Check whether the user has active (non-expired) permission for a specific course. */
export function hasPermission(
  permissions: CoursePermissionWithCourse[] | null,
  courseId: string,
  userId: string,
): boolean {
  if (!permissions) return false;
  return permissions.some(
    (p) =>
      p.course_id.id === courseId &&
      p.user_id === userId &&
      !isPermissionExpired(p),
  );
}

/** Check if a course permission is expired. */
export function isPermissionExpired(
  permission: CoursePermissionWithCourse,
): boolean {
  if (!permission.expires_at) return false;
  return isExpired(permission.expires_at);
}

/** Get days remaining for a course permission. */
export function permissionDaysRemaining(
  permission: CoursePermissionWithCourse,
): number {
  if (!permission.expires_at) return Infinity;
  return daysRemaining(permission.expires_at);
}

/** Check if days-remaining UI should be shown (hidden for free courses). */
export function shouldShowDaysRemaining(
  permission: CoursePermissionWithCourse,
): boolean {
  return permission.course_id.name !== "Free Formations Guide";
}

/** Get progress percentage for a specific course. */
export function getCourseProgress(
  progressData: UserCourseProgressRow[] | null,
  courseId: string,
  userId: string,
): number {
  if (!progressData) return 0;
  const row = progressData.find(
    (p) => p.course_id === courseId && p.user_id === userId,
  );
  if (!row) return 0;
  return progressPercent(row.videos_completed, row.total_videos ?? 0);
}

/** Get progress percentage as a CSS width string (e.g. "75%"). */
export function getCourseProgressWidth(
  progressData: UserCourseProgressRow[] | null,
  courseId: string,
  userId: string,
): string {
  return `${getCourseProgress(progressData, courseId, userId)}%`;
}
